import {clsx} from 'clsx'
import {Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import {ContentWrapper} from '../ContentWrapper'
import type {OptionConfig} from '../OptionConfig'
import type {TaskListStatusFragment$key} from '../shared/__generated__/TaskListStatusFragment.graphql'
import type {HeaderBlockedBySummary$key} from './__generated__/HeaderBlockedBySummary.graphql'
import type {HeaderMetadata$key} from './__generated__/HeaderMetadata.graphql'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import type {HeaderSubIssueSummary$key} from './__generated__/HeaderSubIssueSummary.graphql'
import type {IssueMetadata$key} from './__generated__/IssueMetadata.graphql'
import type {TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {HeaderIssueType, SmallHeaderIssueType} from './HeaderIssueType'
import {type HeaderMenuBaseProps, StickyHeaderMenu} from './HeaderMenu'
import styles from './HeaderMetadata.module.css'
import {HeaderParentTitle} from './HeaderParentTitle'
import {HeaderState} from './HeaderState'
import {HeaderSubIssueSummary} from './HeaderSubIssueSummary'
import {IssueMetadata} from './IssueMetadata'
import {LinkedPullRequestsInternal} from './LinkedPullRequests'
import {RepositoryPill, SmallRepositoryPill} from './RepositoryPill'
import {StickyHeaderTitle} from './StickyHeaderTitle'
import {SmallTrackedBy, TrackedBy} from './TrackedBy'

export type HeaderMetadataProps = {
  headerMetadataKey: HeaderMetadata$key
  optionConfig: OptionConfig
  isSticky: boolean
  headerMetadataSecondaryKey?:
    | TaskListStatusFragment$key
    | TrackedByFragment$key
    | HeaderBlockedBySummary$key
    | HeaderParentTitle$key
    | HeaderSubIssueSummary$key
    | IssueMetadata$key
} & HeaderMenuBaseProps

export function HeaderMetadata({
  headerMetadataKey,
  optionConfig,
  isSticky,
  headerMetadataSecondaryKey,
}: HeaderMetadataProps) {
  const {showRepositoryPill} = optionConfig

  const headerData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderMetadata on Issue {
        url
        repository {
          nameWithOwner
        }
        ...HeaderIssueType
        ...HeaderMenu
        ...HeaderState
        ...StickyHeaderTitle
        ...RepositoryPill
        ...LinkedPullRequests
        ...HeaderSubIssueSummaryWithPrimary
        ...IssueMetadata
      }
    `,
    headerMetadataKey,
  )

  const classNames = []
  if (optionConfig.insideSidePanel) {
    classNames.push('js-second-sticky-header')
  }
  return (
    <>
      {/* Regular header */}
      <ContentWrapper>
        <div
          data-testid={TEST_IDS.issueMetadataFixed}
          className={clsx(styles.metadataContainer, isSticky && styles.metadataContainerSticky)}
        >
          <div className={styles.metadataBorder}>
            <div className={styles.metadataContent}>
              <HeaderState isSticky={isSticky} stateData={headerData} />
              <HeaderIssueType data={headerData} />

              <HeaderSubIssueSummary
                subIssuePrimaryKey={headerData}
                subIssueSecondaryKey={headerMetadataSecondaryKey as HeaderSubIssueSummary$key}
              />

              <HeaderBlockedBySummary
                blockedBySecondaryKey={headerMetadataSecondaryKey as HeaderBlockedBySummary$key}
              />

              <HeaderParentTitle
                parentKey={headerMetadataSecondaryKey as HeaderParentTitle$key}
                optionConfig={optionConfig}
                repositoryNameWithOwner={headerData.repository.nameWithOwner}
              />

              <Suspense>
                <TrackedBy url={headerData.url} trackedByKey={headerMetadataSecondaryKey as TrackedByFragment$key} />
              </Suspense>

              <LinkedPullRequestsInternal issueData={headerData} />
              {showRepositoryPill && <RepositoryPill repositoryPillData={headerData} />}
            </div>
          </div>
        </div>
      </ContentWrapper>
      {/* "Sticky" header */}
      <div
        data-testid={TEST_IDS.issueMetadataSticky}
        id="issue-viewer-sticky-header"
        className={clsx(
          classNames,
          styles.stickyContainer,
          isSticky ? styles.stickyContainerVisible : styles.stickyContainerHidden,
        )}
      >
        <ContentWrapper>
          <div className={styles.stickyContent}>
            <HeaderState isSticky={isSticky} stateData={headerData} />
            <div className={styles.titleAndMetadata}>
              <StickyHeaderTitle headerTitleData={headerData} scrollToTopOnClick={optionConfig.scrollToTopOnClick} />
              <div
                className={clsx(
                  styles.smallMetadataRow,
                  optionConfig.useViewportQueries
                    ? styles.smallMetadataRow_viewport
                    : styles.smallMetadataRow_container,
                )}
              >
                <SmallHeaderIssueType data={headerData} />
                <HeaderSubIssueSummary
                  subIssuePrimaryKey={headerData}
                  subIssueSecondaryKey={headerMetadataSecondaryKey as HeaderSubIssueSummary$key}
                  size="small"
                />
                <HeaderBlockedBySummary
                  blockedBySecondaryKey={headerMetadataSecondaryKey as HeaderBlockedBySummary$key}
                  size="small"
                />
                <HeaderParentTitle
                  parentKey={headerMetadataSecondaryKey as HeaderParentTitle$key}
                  optionConfig={optionConfig}
                  repositoryNameWithOwner={headerData.repository.nameWithOwner}
                  small
                />
                <Suspense>
                  <SmallTrackedBy
                    url={headerData.url}
                    trackedByKey={headerMetadataSecondaryKey as TrackedByFragment$key}
                  />
                </Suspense>
                <LinkedPullRequestsInternal issueData={headerData} isSmall />
                {showRepositoryPill && <SmallRepositoryPill repositoryPillData={headerData} />}
              </div>
            </div>
            <div>
              <StickyHeaderMenu optionConfig={optionConfig} headerMenuData={headerData} />
            </div>
          </div>
        </ContentWrapper>
      </div>
      <IssueMetadata metadataKey={headerData} />
    </>
  )
}

try{ HeaderMetadata.displayName ||= 'HeaderMetadata' } catch {}